import { useRhinoContext } from '@rhino-project/core';
import { useRoles } from '@rhino-project/core/hooks';

// Central place to derive the Harborlight session facts the ported screens need:
// the owner (organization) id used for owner-scoped routing, the user's display
// name, their effective role label, and role-based home path.
//
// Roles in this Rhino app are lowercase ('admin' | 'staff' | 'guest'); the
// source UI labels them uppercase, so we upper-case for display fidelity.
export interface HarborlightSession {
  ownerId?: number;
  ownerParam?: string;
  userName?: string;
  role: 'ADMIN' | 'STAFF' | 'GUEST';
  isStaff: boolean;
  /** Owner-scoped role home path used across the source's roleHomePath(). */
  roleHomePath: string;
}

export function useHarborlightSession(): HarborlightSession {
  const { user, baseOwner, usersRoles } = useRhinoContext();
  const roles = useRoles();

  const ownerId = baseOwner?.id ?? usersRoles?.[0]?.organization?.id;
  const ownerParam = ownerId != null ? String(ownerId) : undefined;

  const isStaff = roles.includes('staff') || roles.includes('admin');
  const role: HarborlightSession['role'] = roles.includes('admin')
    ? 'ADMIN'
    : roles.includes('staff')
      ? 'STAFF'
      : 'GUEST';

  // Guests land on rooms; staff/admin land on the dashboard (owner index).
  const roleHomePath = ownerParam
    ? isStaff
      ? `/${ownerParam}`
      : `/${ownerParam}/rooms`
    : '/';

  return {
    ownerId,
    ownerParam,
    userName: (user as { name?: string } | undefined)?.name,
    role,
    isStaff,
    roleHomePath
  };
}
